import WalleyApi, { WalleyIframePayload, CreateCheckoutOptions, CreateCardRegistrationOptions } from './WalleyApi';

interface WalleyApiEnvelope {
  data?: WalleyRawPayload & { error_en?: string; message?: string };
  error?: string;
  message?: string;
  status?: string;
}

interface WalleyRawPayload {
  publicToken?: string;
  public_token?: string;
  loaderScript?: string;
  loader_script?: string;
  sessionId?: string;
  session_id?: string;
  reference?: string;
}

// Implementation of the Walley payment API surface.
//
// Owns its own fetch instead of reusing V3ApiService.fetchCustom because:
//   - api4 base URL differs from v3 (different path prefix, possibly different host)
//   - api4 uses { data } / { error } envelopes; V3's helper unwraps JSend `result.data`
//   - auth model differs: createCheckout is anonymous, the others use JWT-only
//     bearer (no dbToken header)
class V3WalleyApi implements WalleyApi {
  private walleyApiUrl: string;
  // Lazy JWT lookup so sub-API picks up token refreshes / clears performed
  // by V3ApiService.setDbToken without needing reinstantiation.
  private getJwt: () => string;

  constructor(walleyApiUrl: string, getJwt: () => string) {
    this.walleyApiUrl = walleyApiUrl ?? '';
    this.getJwt = getJwt;
  }

  // Defensive parser for both response conventions:
  //   api4 success     -> { data: { ... } }                         (HTTP 2xx)
  //   api4 error       -> { error: "..." } / { message: "..." }    (HTTP 4xx/5xx)
  private async unwrap(res: Response): Promise<WalleyRawPayload> {
    let json: WalleyApiEnvelope | null = null;
    const text = await res.text();
    if (text) {
      try {
        json = JSON.parse(text) as WalleyApiEnvelope;
      } catch { /* response body is not JSON */ }
    }

    if (!res.ok) {
      const apiError =
        json?.error ??
        json?.message ??
        json?.data?.error_en ??
        `Walley HTTP ${res.status}`;
      throw new Error(apiError);
    }

    if (json?.status === 'fail' || json?.status === 'error') {
      const failMsg = json.data?.error_en ?? json.data?.message ?? 'Walley request failed';
      throw new Error(failMsg);
    }

    return json?.data ?? (json as WalleyRawPayload | null) ?? {};
  }

  // Walley iframe payload arrives in either snake_case (api4) or camelCase
  // (backend). Normalise to camelCase for consumers.
  private toIframePayload(d: WalleyRawPayload): WalleyIframePayload {
    if (!d) {
      throw new Error('Walley response missing payload');
    }
    const publicToken = d.publicToken ?? d.public_token;
    const loaderScript = d.loaderScript ?? d.loader_script;
    if (!publicToken || !loaderScript) {
      throw new Error('Walley response missing public_token or loader_script');
    }
    return {
      publicToken,
      loaderScript,
      sessionId: d.sessionId ?? d.session_id,
      reference: d.reference,
    };
  }

  private jsonHeaders(includeJwt: boolean): Record<string, string> {
    const h: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    if (includeJwt) {
      h.Authorization = 'Bearer ' + this.getJwt();
    }
    return h;
  }

  createCheckout = async (
    reservationId: number,
    options?: CreateCheckoutOptions,
  ): Promise<WalleyIframePayload> => {
    if (!reservationId || reservationId <= 0) {
      throw new Error('Invalid reservation id for Walley checkout');
    }
    const url = this.walleyApiUrl + '/checkout';
    // api4 uses `returnDomain` to resolve the post-payment redirect URL via
    // its WalleyRedirectResolver whitelist (see api4 docs/walley-frontend-
    // integration.md §4.1). Without it, api4 falls back to its WALLEY_RETURN_URL
    // env var, which is configured for the test page — so paying customers
    // would land on the wrong host. Mirrors the `returnDomain` logic in
    // V3ReservationApi.createReservation so both calls agree on the host.
    const returnDomain = V3WalleyApi.resolveReturnDomain();
    // api4 only forwards Subscribe profile to Walley when the FE explicitly
    // opts in via `saveCard: true`. Omit the flag entirely for the regular
    // checkout path so api4 doesn't have to special-case `false` vs absent.
    const bodyPayload: Record<string, unknown> = { reservation_id: reservationId, returnDomain };
    if (options?.saveCard) {
      bodyPayload.saveCard = true;
    }
    const body = JSON.stringify(bodyPayload);
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: this.jsonHeaders(false),
        body,
      });
    } catch (networkErr: unknown) {
      // TypeError "Failed to fetch" surfaces here for CORS, DNS, network-down,
      // and mixed-content failures. Capture both the message and the constructor
      // name so we can tell them apart in devtools.
      throw networkErr;
    }
    const payload = await this.unwrap(res);
    const iframe = this.toIframePayload(payload);
    return iframe;
  };

  createCardRegistration = async (options?: CreateCardRegistrationOptions): Promise<WalleyIframePayload> => {
    if (!this.getJwt()) {
      throw new Error('JWT required for card registration');
    }
    const url = this.walleyApiUrl + '/card-registration';
    const returnDomain = V3WalleyApi.resolveReturnDomain();
    const bodyPayload: Record<string, unknown> = { returnDomain };
    if (options?.business) {
      bodyPayload.business = true;
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: this.jsonHeaders(true),
      body: JSON.stringify(bodyPayload),
    });
    const iframe = this.toIframePayload(await this.unwrap(res));
    return iframe;
  };

  // Mirrors V3ReservationApi.createReservation's returnDomain logic so the
  // reservation-create call and the Walley iframe call agree on the host
  // Walley should redirect back to. api4 maps this through a server-side
  // whitelist (see api4 WalleyRedirectResolver):
  //   '24launcher.rent.24rent.fi' → rentapp:// deep link
  //   'rent.24rent.fi'            → standalone host (dev: dev-rent...)
  //   'localhost:3000'            → http://localhost:3000 (FE local dev)
  //   anything else (incl. 'www.24rent.fi') → www.24rent.fi (dev: dev-frontend.)
  // Note: the localhost branch sends the literal `'localhost:3000'` (with port)
  // because that's the exact whitelist key api4 matches on — the reservation-
  // create call uses bare `'localhost'` against a different (api3/Silex)
  // backend whitelist, so we deliberately diverge here rather than sharing.
  private static resolveReturnDomain(): string {
    const pageUrl = window.location.hostname;
    let returnDomain = pageUrl.includes('dev-frontend.24rent.fi')
      || pageUrl.includes('www.24rent.fi') ? 'www.24rent.fi' :
        pageUrl.includes('localhost') ? 'localhost:3000' : 'rent.24rent.fi';
    if (returnDomain !== 'localhost:3000' && sessionStorage.getItem('redirectRef') === 'launcher') {
      returnDomain = '24launcher.' + returnDomain;
    }
    return returnDomain;
  }

}

export default V3WalleyApi;
